import React from 'react';
import SubpageNav from '../layout/SubpageNav';
import Footer from '../layout/Footer';
import Container from '../layout/Container';
import Eyebrow from '../ui/Eyebrow';
import Button from '../ui/Button';
import PeakLogo from '../ui/PeakLogo';
import { colors, font } from '../../lib/tokens';

const h2: React.CSSProperties = {
  fontFamily: font.sans,
  fontWeight: 500,
  fontSize: 44,
  letterSpacing: -1.5,
  lineHeight: 1.02,
  color: colors.ink,
  margin: '16px 0 0',
};

const sectionIntro: React.CSSProperties = {
  fontSize: 16,
  lineHeight: 1.7,
  color: colors.muted,
  marginTop: 20,
};

const monoLabel: React.CSSProperties = {
  fontFamily: font.mono,
  fontSize: 11,
  letterSpacing: 0.6,
  textTransform: 'uppercase',
  color: colors.muted,
};

const SWATCHES = [
  { name: 'Ink', hex: colors.ink, usage: 'Headings, primary text, the mark, dark sections', border: false },
  { name: 'Ink 2', hex: colors.ink2, usage: 'Body copy on light surfaces', border: false },
  { name: 'Muted', hex: colors.muted, usage: 'Secondary copy, the italic emphasis device', border: false },
  { name: 'Dim', hex: colors.dim, usage: 'Tertiary copy, footer text on ink', border: false },
  { name: 'Blue', hex: colors.blue, usage: 'Accent: links, eyebrows, the fault band in the mark', border: false },
  { name: 'Blue soft', hex: colors.blueSoft, usage: 'Tinted panels and highlights', border: true },
  { name: 'Ember', hex: colors.ember, usage: 'Reserved for naming the problem, never decoration', border: false },
  { name: 'Background', hex: colors.bg, usage: 'Warm off-white page background, never pure grey', border: true },
  { name: 'Surface', hex: colors.surface, usage: 'Cards and alternating sections', border: true },
  { name: 'Hairline', hex: colors.hair, usage: 'Borders and separators', border: true },
  { name: 'Ice blue', hex: '#93C5FD', usage: 'Accent on dark backgrounds (eyebrows, the inverse mark)', border: false },
];

const DONTS = [
  { label: "Don't recolor the mark", render: <PeakLogo color="#16A34A" accent="#EAB308" size={64} showText={false} /> },
  {
    label: "Don't stretch or squash it",
    render: (
      <span style={{ display: 'inline-block', transform: 'scaleX(1.6)' }}>
        <PeakLogo size={64} showText={false} />
      </span>
    ),
  },
  {
    label: "Don't rotate it",
    render: (
      <span style={{ display: 'inline-block', transform: 'rotate(-24deg)' }}>
        <PeakLogo size={64} showText={false} />
      </span>
    ),
  },
  {
    label: "Don't add shadows or effects",
    render: (
      <span style={{ display: 'inline-block', filter: 'drop-shadow(4px 6px 4px rgba(11,18,32,0.45))' }}>
        <PeakLogo size={64} showText={false} />
      </span>
    ),
  },
  {
    label: "Don't place it on busy color",
    render: (
      <span
        style={{
          display: 'inline-flex',
          padding: 14,
          borderRadius: 8,
          background: 'linear-gradient(135deg, #F97316, #DB2777, #7C3AED)',
        }}
      >
        <PeakLogo size={56} showText={false} />
      </span>
    ),
  },
  {
    label: "Don't retype the wordmark",
    render: (
      <span style={{ fontFamily: 'Georgia, serif', fontSize: 26, fontWeight: 700, color: colors.ink }}>
        KATLACODE
      </span>
    ),
  },
];

const TYPE_SCALE = [
  { label: 'Display', spec: 'Inter Tight 500 · clamp(40–64px) · −2 tracking', sample: 'Coding, inferred', size: 52, weight: 500, ls: -2 },
  { label: 'Heading', spec: 'Inter Tight 500 · 44px · −1.5 tracking · 1.02 line', sample: 'The company at a glance', size: 44, weight: 500, ls: -1.5 },
  { label: 'Body', spec: 'Inter Tight 400 · 16–18px · 1.65–1.7 line', sample: 'The system reads physician notes and suggests the right diagnosis codes.', size: 17, weight: 400, ls: 0 },
  { label: 'Label', spec: 'JetBrains Mono · 11px · +0.6 tracking · uppercase', sample: 'CLINICAL VALIDATION · STEP 02', size: 11, weight: 400, ls: 0.6, mono: true },
];

function Section({
  background,
  children,
}: {
  background: string;
  children: React.ReactNode;
}) {
  return (
    <section style={{ background, padding: '120px 0', borderTop: `1px solid ${colors.hair}` }}>
      <Container>{children}</Container>
    </section>
  );
}

export default function BrandManualPage() {
  React.useEffect(() => {
    document.title = 'Brand manual · KatlaCode';
  }, []);

  return (
    <div className="min-h-screen bg-kc-bg text-kc-ink">
      <SubpageNav current="brand" />

      {/* Intro */}
      <section style={{ background: colors.bg, padding: '96px 0 120px' }}>
        <Container>
          <div className="grid grid-cols-1 md:grid-cols-[1fr_1.3fr] gap-12 md:gap-24">
            <div>
              <Eyebrow>Brand manual</Eyebrow>
              <h1 style={{ ...h2, fontSize: 'clamp(40px, 5vw, 56px)' }}>
                Precise, calm,{' '}
                <span style={{ fontStyle: 'italic', color: colors.muted }}>clinical</span>
              </h1>
            </div>
            <div>
              <p style={{ fontSize: 18, lineHeight: 1.7, color: colors.ink2, margin: '8px 0 28px' }}>
                KatlaCode lives inside hospitals, and the brand behaves accordingly: quiet
                surfaces, exact typography, one blue accent, and no decoration without a job.
                This manual covers the mark, color, type, and voice. Logo files are in the
                press kit.
              </p>
              <Button variant="primary" href="/press">
                Get the assets
              </Button>
            </div>
          </div>
        </Container>
      </section>

      {/* The mark */}
      <Section background={colors.surface}>
        <div className="grid grid-cols-1 md:grid-cols-[1fr_1.3fr] gap-12 md:gap-24">
          <div>
            <Eyebrow>The mark</Eyebrow>
            <h2 style={h2}>Twin peaks on a fault line</h2>
            <p style={sectionIntro}>
              The company is named for Katla, the volcano under Mýrdalsjökull. The mark is two
              peaks offset along a fault, with the accent color exposed in the crack between
              them. The fault line is the product: the seam between what physicians write and
              what the record needs.
            </p>
            <p style={sectionIntro}>
              The wordmark is Inter Tight at weight 600, always lowercase:{' '}
              <strong style={{ color: colors.ink, fontWeight: 600 }}>katlacode</strong>. In
              running text, write <strong style={{ color: colors.ink, fontWeight: 600 }}>KatlaCode</strong>.
            </p>
          </div>
          <div style={{ display: 'grid', gap: 24 }}>
            <div
              style={{
                background: colors.bg,
                border: `1px solid ${colors.hair}`,
                borderRadius: 12,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '64px 32px',
              }}
            >
              <img
                src="/press-kit/logo/katlacode-lockup.png"
                alt="katlacode lockup, peak mark and wordmark"
                style={{ width: 420, maxWidth: '100%', height: 'auto' }}
              />
            </div>
            <div className="grid grid-cols-2 gap-6">
              <div
                style={{
                  background: colors.bg,
                  border: `1px solid ${colors.hair}`,
                  borderRadius: 12,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 16,
                  padding: '40px 24px 24px',
                }}
              >
                <PeakLogo size={64} showText={false} />
                <span style={monoLabel}>On light · ink + blue</span>
              </div>
              <div
                style={{
                  background: colors.ink,
                  borderRadius: 12,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 16,
                  padding: '40px 24px 24px',
                }}
              >
                <PeakLogo size={64} showText={false} color="#FFF" accent="#93C5FD" />
                <span style={{ ...monoLabel, color: colors.dim }}>On dark · white + ice</span>
              </div>
            </div>
          </div>
        </div>
      </Section>

      {/* Clear space & minimum size */}
      <Section background={colors.bg}>
        <div className="grid grid-cols-1 md:grid-cols-[1fr_1.3fr] gap-12 md:gap-24">
          <div>
            <Eyebrow>Clear space</Eyebrow>
            <h2 style={h2}>Give the peaks room</h2>
            <p style={sectionIntro}>
              Keep a margin of at least half the mark's height clear on every side, with no
              text, images, or other logos inside it. Never render the mark smaller than 24&nbsp;px
              on screen or 8&nbsp;mm in print; below that, drop to the mark alone (no
              wordmark).
            </p>
          </div>
          <div
            style={{
              background: colors.surface,
              border: `1px solid ${colors.hair}`,
              borderRadius: 12,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 48,
            }}
          >
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 48,
                border: `1px dashed ${colors.dim}`,
                borderRadius: 4,
                position: 'relative',
              }}
            >
              <PeakLogo size={96} showText={false} />
              <span
                style={{
                  ...monoLabel,
                  position: 'absolute',
                  top: 14,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  whiteSpace: 'nowrap',
                }}
              >
                ½ height
              </span>
            </span>
          </div>
        </div>
      </Section>

      {/* Misuse */}
      <Section background={colors.surface}>
        <Eyebrow color={colors.ember}>Misuse</Eyebrow>
        <h2 style={h2}>What not to do</h2>
        <p style={{ ...sectionIntro, maxWidth: 560, marginBottom: 48 }}>
          The mark ships in two colorways, ink with blue and white with ice blue. If a layout
          tempts you toward any of these, use the plain wordmark instead.
        </p>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
          {DONTS.map((item) => (
            <div
              key={item.label}
              style={{
                border: `1px solid ${colors.hair}`,
                borderRadius: 12,
                padding: '36px 20px 20px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 24,
                background: colors.bg,
              }}
            >
              <div style={{ height: 88, display: 'flex', alignItems: 'center' }}>{item.render}</div>
              <div
                style={{
                  ...monoLabel,
                  color: colors.ember,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  textAlign: 'center',
                }}
              >
                <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true">
                  <path d="M2 2l8 8M10 2l-8 8" stroke={colors.ember} strokeWidth="1.5" strokeLinecap="round" />
                </svg>
                {item.label}
              </div>
            </div>
          ))}
        </div>
      </Section>

      {/* Color */}
      <Section background={colors.bg}>
        <div className="grid grid-cols-1 md:grid-cols-[1fr_1.3fr] gap-12 md:gap-24">
          <div>
            <Eyebrow>Color</Eyebrow>
            <h2 style={h2}>A clinical palette, one accent</h2>
            <p style={sectionIntro}>
              Light, warm neutrals carry almost everything; blue is the single working accent.
              Ember appears only when naming the problem we solve. Sections alternate between
              background and surface, with at most one dark ink anchor per page.
            </p>
          </div>
          <div>
            {SWATCHES.map((swatch) => (
              <div
                key={swatch.name}
                className="kc-swatch-row"
                style={{ padding: '10px 0', borderBottom: `1px solid ${colors.hair2}` }}
              >
                <span
                  style={{
                    gridArea: 'chip',
                    width: 44,
                    height: 32,
                    borderRadius: 6,
                    background: swatch.hex,
                    border: swatch.border ? `1px solid ${colors.hair}` : 'none',
                    display: 'inline-block',
                  }}
                />
                <span style={{ gridArea: 'name', fontSize: 14, fontWeight: 600, color: colors.ink }}>
                  {swatch.name}
                </span>
                <span style={{ ...monoLabel, gridArea: 'hex', textTransform: 'none' }}>{swatch.hex}</span>
                <span style={{ gridArea: 'usage', fontSize: 13, color: colors.muted }}>{swatch.usage}</span>
              </div>
            ))}
          </div>
        </div>
      </Section>

      {/* Typography */}
      <Section background={colors.surface}>
        <Eyebrow>Typography</Eyebrow>
        <h2 style={h2}>Inter Tight speaks, JetBrains Mono annotates</h2>
        <p style={{ ...sectionIntro, maxWidth: 560, marginBottom: 48 }}>
          Inter Tight carries all reading text. Headings sit at weight 500 with tight
          negative tracking; body runs at 400. JetBrains Mono is the instrument label, used
          for eyebrows, step numbers, timelines, citations, and data. It never sets a full
          paragraph.
        </p>
        <div>
          {TYPE_SCALE.map((row) => (
            <div
              key={row.label}
              className="grid grid-cols-1 md:grid-cols-[260px_1fr] gap-2 md:gap-12"
              style={{ padding: '28px 0', borderBottom: `1px solid ${colors.hair2}`, alignItems: 'center' }}
            >
              <div>
                <div style={{ fontSize: 14, fontWeight: 600, color: colors.ink }}>{row.label}</div>
                <div style={{ ...monoLabel, textTransform: 'none', marginTop: 4 }}>{row.spec}</div>
              </div>
              <div
                style={{
                  fontFamily: row.mono ? font.mono : font.sans,
                  fontSize: row.size,
                  fontWeight: row.weight,
                  letterSpacing: row.ls,
                  lineHeight: 1.15,
                  color: colors.ink,
                  textTransform: row.mono ? 'uppercase' : 'none',
                  overflowWrap: 'break-word',
                }}
              >
                {row.sample}
              </div>
            </div>
          ))}
        </div>
      </Section>

      {/* Voice */}
      <Section background={colors.bg}>
        <div className="grid grid-cols-1 md:grid-cols-[1fr_1.3fr] gap-12 md:gap-24">
          <div>
            <Eyebrow>Voice</Eyebrow>
            <h2 style={h2}>Say less, mean it</h2>
            <p style={sectionIntro}>
              We write like a good discharge note: complete, unhurried, and free of adjectives
              that don't earn their place. Exclamation marks and "revolutionary" are out.
              Claims come with the workflow or the number that backs them.
            </p>
          </div>
          <div style={{ display: 'grid', gap: 20 }}>
            <div
              style={{
                background: colors.surface,
                border: `1px solid ${colors.hair}`,
                borderRadius: 12,
                padding: '28px 32px',
              }}
            >
              <span style={monoLabel}>The emphasis device</span>
              <p
                style={{
                  fontFamily: font.sans,
                  fontWeight: 500,
                  fontSize: 30,
                  letterSpacing: -1,
                  lineHeight: 1.15,
                  color: colors.ink,
                  margin: '14px 0 12px',
                }}
              >
                Coding, <span style={{ fontStyle: 'italic', color: colors.muted }}>inferred</span>
              </p>
              <p style={{ fontSize: 14, lineHeight: 1.65, color: colors.muted, margin: 0 }}>
                One word per heading may turn italic and muted, the word that carries the
                claim. Use it at most once per section, and never on the wordmark.
              </p>
            </div>
            <div
              style={{
                background: colors.surface,
                border: `1px solid ${colors.hair}`,
                borderRadius: 12,
                padding: '28px 32px',
              }}
            >
              <span style={monoLabel}>House rules</span>
              <ul style={{ margin: '14px 0 0', paddingLeft: 18, fontSize: 15, lineHeight: 1.9, color: colors.ink2 }}>
                <li>"KatlaCode" in prose; "katlacode" only in the wordmark.</li>
                <li>Physicians, not "users." Hospitals, not "the healthcare space."</li>
                <li>Ember color and the word "problem" travel together.</li>
                <li>Numbers in JetBrains Mono when presented as data.</li>
              </ul>
            </div>
          </div>
        </div>
      </Section>

      <Footer />
    </div>
  );
}
